import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, FileText, User, Wrench } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useCreateOperation, useUploadOperationFile, useUserGroups } from '@/core/api';
import type { PayloadApiError } from '@/core/api/client';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { MultiSearchCombobox, SearchCombobox } from '@/shared/components/inputs';
import { DetailCard, DetailPageLayout } from '@/shared/components/layout';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@/shared/components/ui';
import { isFileTooLargeError } from '@/shared/utils';
import {
  canCreateService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';

import { createOperationCreateSchema, type OperationCreateFormData } from '../schema';

const getUserFullNameLabel = (user: unknown) => {
  if (!user || typeof user !== 'object') return '';
  const firstName = (user as any).firstName as string | undefined;
  const lastName = (user as any).lastName as string | undefined;
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  return fullName || String((user as any).id || '');
};

export function OperationCreate() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();
  const reportInputRef = useRef<HTMLInputElement>(null);

  const group = resolveUserGroup(user);
  const canCreateOperations = group ? canCreateService(group, 'operations') : false;
  const createOperation = useCreateOperation();
  const uploadOperationFile = useUploadOperationFile();
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });

  const gmnGroupIds =
    userGroupsData?.docs.filter((group) => group.domain === 'gmn').map((group) => group.id) ?? [];
  const customerGroupIds =
    userGroupsData?.docs.filter((group) => group.domain === 'customer').map((group) => group.id) ??
    [];
  const heliProviderGroupIds =
    userGroupsData?.docs
      .filter((group) => group.key === 'heli-provider')
      .map((group) => group.id) ?? [];
  const dispatcherGroupIds =
    userGroupsData?.docs
      .filter((group) => group.key === 'juh-dispatcher')
      .map((group) => group.id) ?? [];

  const schema = useMemo(() => createOperationCreateSchema(t), [t]);

  const form = useForm<OperationCreateFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      customer: '',
      responsibleGmnUser: resolveUserGroupDomain(user) === 'gmn' ? user?.id : '',
      assignedCustomerUser: '',
      heliProviderUsers: [],
      dispatcherUsers: [],
      reportFile: undefined as unknown as File,
    },
  });

  const selectedCustomer = form.watch('customer');

  useEffect(() => {
    form.setValue('assignedCustomerUser', '');
  }, [selectedCustomer, form]);

  const submitForm = () => {
    const formElement = document.getElementById('operation-create-form') as HTMLFormElement | null;
    formElement?.requestSubmit();
  };

  const onSubmit = async (data: OperationCreateFormData) => {
    if (!canCreateOperations) return;

    try {
      const uploaded = await uploadOperationFile.mutateAsync({
        file: data.reportFile,
        data: {
          kind: 'report',
        },
      });

      const reportFileId = uploaded.doc.id;

      const created = await createOperation.mutateAsync({
        title: data.title,
        ...(data.description?.trim() ? { description: data.description.trim() } : {}),
        customer: data.customer,
        responsibleGmnUser: data.responsibleGmnUser,
        assignedCustomerUser: data.assignedCustomerUser,
        heliProviderUsers: data.heliProviderUsers ?? [],
        dispatcherUsers: data.dispatcherUsers ?? [],
        reportFile: reportFileId,
      });

      toast.success(t('operations.toasts.created'));
      navigate(`/gmn/operations/${created.doc.id}`);
    } catch (_error) {
      if (isFileTooLargeError(_error)) {
        toast.error(t('operations.toasts.fileTooLarge'));
        return;
      }

      toast.error(t('operations.toasts.createFailed'));
      const err = _error as PayloadApiError;
      if (err?.response?.errors?.length) {
        err.response.errors.forEach((apiErr) => {
          const message =
            apiErr && typeof apiErr === 'object' && 'message' in apiErr
              ? String((apiErr as { message?: unknown }).message)
              : String(apiErr);
          toast.error(message);
        });
      }
    }
  };

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref: '/gmn/operations',
        backLabel: t('operations.backToOverview'),
        title: t('operations.newOperation'),
        subtitle: t('operations.createNewOperationHint'),
        icon: Wrench,
        isCreateMode: true,
        secondaryAction: {
          label: t('common.discard'),
          onClick: () => navigate('/gmn/operations'),
        },
        primaryAction: {
          label: createOperation.isPending ? t('common.creating') : t('common.create'),
          onClick: submitForm,
          disabled:
            !canCreateOperations || createOperation.isPending || uploadOperationFile.isPending,
          loading: createOperation.isPending || uploadOperationFile.isPending,
        },
      }}
    >
      <Form {...form}>
        <form
          id="operation-create-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid gap-6 lg:grid-cols-2"
        >
          <DetailCard icon={FileText} title={t('operations.details.title')}>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel required>{t('operations.fields.title')}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!canCreateOperations} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="space-y-2 md:col-span-2">
                    <FormLabel>{t('operations.fields.description')}</FormLabel>
                    <FormControl>
                      <Textarea {...field} disabled={!canCreateOperations} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DetailCard>

          <DetailCard icon={Building2} title={t('operations.assignment.title')}>
            <div className="space-y-6">
              <div className="space-y-2">
                <FormLabel required>{t('customers.customer')}</FormLabel>
                <FormField
                  control={form.control}
                  name="customer"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <SearchCombobox
                          value={field.value || undefined}
                          onSelect={field.onChange}
                          collectionKey="customers"
                          searchKey="name"
                          disabled={!canCreateOperations}
                          texts={{
                            selectItemMsg: t('operations.assignment.selectCustomer'),
                            searchPlaceholder: t('operations.assignment.searchCustomer'),
                          }}
                          icon={Building2}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel required>{t('operations.assignment.assignedCustomerUser')}</FormLabel>
                <FormField
                  control={form.control}
                  name="assignedCustomerUser"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <SearchCombobox
                          value={field.value || undefined}
                          onSelect={field.onChange}
                          collectionKey="users"
                          searchKey="email"
                          getItemLabel={getUserFullNameLabel}
                          disabled={!canCreateOperations || !selectedCustomer}
                          texts={{
                            selectItemMsg: t('operations.assignment.selectCustomerUser'),
                            searchPlaceholder: t('operations.assignment.searchUsers'),
                          }}
                          icon={User}
                          customWhere={{
                            and: [
                              { userGroup: { in: customerGroupIds } },
                              ...(selectedCustomer
                                ? [{ customer: { equals: selectedCustomer } }]
                                : []),
                            ],
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel required>{t('operations.assignment.responsibleGmnUser')}</FormLabel>
                <FormField
                  control={form.control}
                  name="responsibleGmnUser"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <SearchCombobox
                          value={field.value || undefined}
                          onSelect={field.onChange}
                          collectionKey="users"
                          searchKey="email"
                          getItemLabel={getUserFullNameLabel}
                          disabled={!canCreateOperations}
                          texts={{
                            selectItemMsg: t('operations.assignment.selectResponsibleUser'),
                            searchPlaceholder: t('operations.assignment.searchUsers'),
                          }}
                          icon={User}
                          customWhere={{ userGroup: { in: gmnGroupIds } }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel>{t('operations.assignment.heliProviderUsers')}</FormLabel>
                <FormField
                  control={form.control}
                  name="heliProviderUsers"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <MultiSearchCombobox
                          value={field.value ?? []}
                          onSelect={field.onChange}
                          collectionKey="users"
                          searchKey="email"
                          getItemLabel={getUserFullNameLabel}
                          disabled={!canCreateOperations}
                          texts={{
                            selectItemMsg: t('operations.assignment.selectHeliProviderUsers'),
                            searchPlaceholder: t('operations.assignment.searchUsers'),
                          }}
                          icon={User}
                          customWhere={{ userGroup: { in: heliProviderGroupIds } }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel>{t('operations.assignment.dispatcherUsers')}</FormLabel>
                <FormField
                  control={form.control}
                  name="dispatcherUsers"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <MultiSearchCombobox
                          value={field.value ?? []}
                          onSelect={field.onChange}
                          collectionKey="users"
                          searchKey="email"
                          getItemLabel={getUserFullNameLabel}
                          disabled={!canCreateOperations}
                          texts={{
                            selectItemMsg: t('operations.assignment.selectDispatcherUsers'),
                            searchPlaceholder: t('operations.assignment.searchUsers'),
                          }}
                          icon={User}
                          customWhere={{ userGroup: { in: dispatcherGroupIds } }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </DetailCard>

          <DetailCard
            icon={FileText}
            title={t('operations.report.title')}
            className="lg:col-span-2"
          >
            <p className="text-sm text-muted-foreground mb-6">
              {t('operations.report.description')}
            </p>
            <FormField
              control={form.control}
              name="reportFile"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="space-y-3">
                      {field.value ? (
                        <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="size-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                              <FileText className="size-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{field.value.name}</div>
                              <div className="text-xs text-muted-foreground">PDF</div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!canCreateOperations}
                            onClick={() => reportInputRef.current?.click()}
                          >
                            {t('operations.files.replace')}
                          </Button>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-5 flex items-center gap-3 text-sm text-muted-foreground">
                          <FileText className="size-4" />
                          {t('operations.files.empty')}
                        </div>
                      )}
                      {!field.value && (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!canCreateOperations}
                          onClick={() => reportInputRef.current?.click()}
                        >
                          {t('operations.files.upload')}
                        </Button>
                      )}
                      <input
                        ref={reportInputRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        disabled={!canCreateOperations}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          field.onChange(file);
                          event.currentTarget.value = '';
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DetailCard>
        </form>
      </Form>
    </DetailPageLayout>
  );
}
